/**
 * HTTP MIDI Transport - MIDI communication via external midi-server
 *
 * This transport implements the MidiTransport interface using HTTP requests
 * to an external midi-server process. It uses Server-Sent Events (SSE) for
 * real-time incoming MIDI messages.
 *
 * This transport is designed for E2E testing where Web MIDI API's SysEx
 * permissions cause Chrome to crash in Playwright.
 */
async function fetchJson(serverUrl, path, options) {
    const res = await fetch(`${serverUrl}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options?.headers,
        },
    });
    if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text}`);
    }
    return res.json();
}
function createHttpMidiAdapter(serverUrl, inputId, outputId) {
    const listeners = new Set();
    // Connect SSE for incoming messages
    const eventSource = new EventSource(`${serverUrl}/port/${encodeURIComponent(inputId)}/events`);
    eventSource.addEventListener('midi', (e) => {
        try {
            const data = JSON.parse(e.data);
            const bytes = data.bytes;
            // Only dispatch SysEx messages (starting with 0xF0)
            if (bytes.length > 0 && bytes[0] === 0xf0) {
                listeners.forEach((cb) => cb(bytes));
            }
        }
        catch (err) {
            console.error('[HttpMidiTransport] Failed to parse SSE event:', err);
        }
    });
    eventSource.onerror = (err) => {
        console.error('[HttpMidiTransport] SSE error:', err);
    };
    // Serialize HTTP POSTs and await each to completion.
    // The browser's event loop may batch or delay fire-and-forget fetch() calls,
    // causing SDS closed-loop transfers to fail (device ACKs packets but doesn't
    // create the sample). Awaiting each send ensures the MIDI message is fully
    // delivered before the next send is queued.
    let sendQueue = Promise.resolve();
    const adapter = {
        send: (message) => {
            sendQueue = sendQueue.then(async () => {
                try {
                    const res = await fetch(`${serverUrl}/port/${encodeURIComponent(outputId)}/send`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ message }),
                    });
                    await res.text();
                }
                catch (err) {
                    console.error('[HttpMidiTransport] Send error:', err);
                }
            });
        },
        onSysEx: (callback) => {
            listeners.add(callback);
        },
        removeSysExListener: (callback) => {
            listeners.delete(callback);
        },
    };
    return { adapter, eventSource };
}
export function createHttpMidiTransport(config) {
    const { serverUrl } = config;
    let stateHandler = null;
    const browserInfo = {
        supported: true,
        browser: 'HTTP MIDI',
        notes: 'MIDI via external midi-server process. Used for E2E testing.',
    };
    return {
        kind: 'http-midi',
        isSupported: () => true,
        getBrowserInfo: () => browserInfo,
        initialize: async () => {
            const ports = await fetchJson(serverUrl, '/ports');
            const inputs = ports.inputs.map((name, idx) => ({
                id: `input-${idx}`,
                name,
                state: 'connected',
            }));
            const outputs = ports.outputs.map((name, idx) => ({
                id: `output-${idx}`,
                name,
                state: 'connected',
            }));
            return { inputs, outputs, sysExEnabled: true };
        },
        refresh: async () => {
            const ports = await fetchJson(serverUrl, '/ports');
            const inputs = ports.inputs.map((name, idx) => ({
                id: `input-${idx}`,
                name,
                state: 'connected',
            }));
            const outputs = ports.outputs.map((name, idx) => ({
                id: `output-${idx}`,
                name,
                state: 'connected',
            }));
            return { inputs, outputs, sysExEnabled: true };
        },
        onStateChange: (handler) => {
            stateHandler = handler;
        },
        connect: async (inputId, outputId) => {
            // Get port list to resolve names
            const ports = await fetchJson(serverUrl, '/ports');
            // Parse input/output indices from IDs
            const inputIdx = parseInt(inputId.replace('input-', ''), 10);
            const outputIdx = parseInt(outputId.replace('output-', ''), 10);
            const inputName = ports.inputs[inputIdx];
            const outputName = ports.outputs[outputIdx];
            if (!inputName) {
                throw new Error(`Input port not found: ${inputId}`);
            }
            if (!outputName) {
                throw new Error(`Output port not found: ${outputId}`);
            }
            // Open ports on server
            const inputResult = await fetchJson(serverUrl, `/port/${encodeURIComponent(inputId)}`, {
                method: 'POST',
                body: JSON.stringify({ name: inputName, type: 'input' }),
            });
            if (!inputResult.success) {
                throw new Error(`Failed to open input port: ${inputResult.error}`);
            }
            const outputResult = await fetchJson(serverUrl, `/port/${encodeURIComponent(outputId)}`, {
                method: 'POST',
                body: JSON.stringify({ name: outputName, type: 'output' }),
            });
            if (!outputResult.success) {
                throw new Error(`Failed to open output port: ${outputResult.error}`);
            }
            // Create adapter with SSE connection
            const { adapter, eventSource } = createHttpMidiAdapter(serverUrl, inputId, outputId);
            const inputInfo = {
                id: inputId,
                name: inputName,
                state: 'connected',
            };
            const outputInfo = {
                id: outputId,
                name: outputName,
                state: 'connected',
            };
            return {
                adapter,
                inputInfo,
                outputInfo,
                disconnect: async () => {
                    // Close SSE connection
                    eventSource.close();
                    // Close ports on server
                    await fetch(`${serverUrl}/port/${encodeURIComponent(inputId)}`, {
                        method: 'DELETE',
                    }).catch(() => {
                        // Ignore errors on cleanup
                    });
                    await fetch(`${serverUrl}/port/${encodeURIComponent(outputId)}`, {
                        method: 'DELETE',
                    }).catch(() => {
                        // Ignore errors on cleanup
                    });
                },
            };
        },
    };
}
//# sourceMappingURL=httpMidiTransport.js.map